import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import Navbar from './components/Navbar';
import SearchBar from './components/SearchBar';
import ReviewFeed from './components/ReviewFeed';
import ReviewModal from './components/ReviewModal';
import Login from './components/Login';
import UserProfile from './components/UserProfile';
import { AuthContext } from './contexts/AuthContext';
import { checkRateLimit, commitRateLimit, consumeRateLimit } from './services/localRateLimitService';
import {
  browseReviews,
  createReview,
  getPlateStats,
  searchReviews,
} from './services/firestoreService';

const GUEST_REVIEW_USED_KEY = 'ratethatdriver_guest_review_used';

function AppContent() {
  const { user } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPlate, setCurrentPlate] = useState('');
  const [currentState, setCurrentState] = useState('');
  const [selectedSortFilter] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginPromptReason, setLoginPromptReason] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  // feedMode: 'search' shows plate/state results; 'browse' shows /reviews/browse results
  const [feedMode, setFeedMode] = useState('search');
  const [feedLabel, setFeedLabel] = useState('Review Feed:');
  const [browsePage, setBrowsePage] = useState(1);
  const [browseStateFilter, setBrowseStateFilter] = useState('');
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageLimit, setPageLimit] = useState(() => window.innerWidth >= 768 ? 20 : 10);
  const pagingRef = useRef(false);
  const reviewFeedRef = useRef(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    const handler = () => {
      const newLimit = window.innerWidth >= 768 ? 20 : 10;
      setPageLimit(prev => {
        if (prev !== newLimit) {
          setCurrentPage(1);
          setBrowsePage(1);
        }
        return newLimit;
      });
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchReviews = useCallback(async (plate, state, page = 1, sort = 'recent') => {
    const isPaging = pagingRef.current;
    pagingRef.current = false;
    if (isPaging) {
      setPageLoading(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const data = await searchReviews({
        plate,
        state,
        page,
        pageSize: pageLimit,
        sort,
      });
      setReviews(data.reviews);
      setPagination(data.pagination);
    } catch (err) {
      console.error('[Search error]', err);
      setError('Unable to load reviews. Please try again.');
      setReviews([]);
      setPagination(null);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [pageLimit]);

  const fetchStats = useCallback(async (plate, state) => {
    try {
      const data = await getPlateStats(plate, state);
      setStats(data);
    } catch (err) {
      setStats(null);
    }
  }, []);

  const fetchBrowseReviews = useCallback(async (state, page = 1) => {
    const isPaging = pagingRef.current;
    pagingRef.current = false;
    if (isPaging) {
      setPageLoading(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const data = await browseReviews({ state, page, pageSize: pageLimit });
      setReviews(data.reviews);
      setPagination(data.pagination);
    } catch (err) {
      console.error('[Browse error]', err);
      setError('Unable to load reviews. Please try again.');
      setReviews([]);
      setPagination(null);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [pageLimit]);

  useEffect(() => {
    if (feedMode === 'search' && currentPlate && currentState) {
      fetchReviews(currentPlate, currentState, currentPage, selectedSortFilter);
      fetchStats(currentPlate, currentState);
    }
  }, [currentPlate, currentState, currentPage, selectedSortFilter, feedMode, fetchReviews, fetchStats]);

  useEffect(() => {
    if (feedMode === 'browse') {
      fetchBrowseReviews(browseStateFilter, browsePage);
    }
  }, [feedMode, browseStateFilter, browsePage, fetchBrowseReviews]);

  const handleSearch = useCallback(async (plate, state) => {
    try {
      consumeRateLimit('searches');
    } catch (error) {
      setToast({ type: 'error', message: 'Too many submissions, please wait' });
      return;
    }

    setFeedMode('search');
    setFeedLabel('Review Feed:');
    setCurrentPlate(plate);
    setCurrentState(state);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    pagingRef.current = true;
    if (feedMode === 'browse') {
      setBrowsePage(page);
    } else {
      setCurrentPage(page);
    }
    if (reviewFeedRef.current) {
      reviewFeedRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [feedMode]);

  const handleStateSelect = useCallback((stateCode) => {
    setFeedMode('browse');
    setBrowseStateFilter(stateCode);
    setBrowsePage(1);
    setFeedLabel(`Reviews — ${stateCode}`);
    setStats(null);
  }, []);

  const handleReviewsClick = useCallback(() => {
    setFeedMode('browse');
    setBrowseStateFilter('');
    setBrowsePage(1);
    setFeedLabel('All Reviews');
    setStats(null);
  }, []);

  const handleReviewSubmit = useCallback(async (formData) => {
    setSubmitting(true);
    setUploadProgress(0);
    try {
      checkRateLimit('reviews');

      const isGuest = !user;
      const guestReviewAlreadyUsed = localStorage.getItem(GUEST_REVIEW_USED_KEY) === '1';
      if (isGuest && guestReviewAlreadyUsed) {
        throw new Error('Please sign in to continue');
      }

      const plate = formData.get('plate');
      const state = formData.get('state');
      const title = formData.get('title');
      const text = formData.get('text');
      const files = formData.getAll('media');

      await createReview({
        plate,
        state,
        title,
        text,
        files,
        isAnonymous: isGuest,
        onProgress: setUploadProgress,
      });

      commitRateLimit('reviews');

      if (isGuest) {
        localStorage.setItem(GUEST_REVIEW_USED_KEY, '1');
      }

      setToast({ type: 'success', message: 'Review submitted successfully.' });
      setShowModal(false);

      if (formData.get('plate') === currentPlate && formData.get('state') === currentState) {
        fetchReviews(currentPlate, currentState, currentPage, selectedSortFilter);
        fetchStats(currentPlate, currentState);
      }
    } catch (err) {
      const requiresSignIn = err?.message && err.message.includes('Please sign in');

      if (requiresSignIn) {
        setShowModal(false);
        setLoginPromptReason('guest-limit');
        setShowLoginModal(true);
        return;
      }

      const safeMessage = err.message || 'Something went wrong, please try again';
      setToast({ type: 'error', message: safeMessage });
      throw err;
    } finally {
      setSubmitting(false);
      setTimeout(() => setUploadProgress(0), 300);
    }
  }, [user, currentPlate, currentState, fetchReviews, currentPage, selectedSortFilter, fetchStats]);

  const handleAddReviewClick = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleOpenLoginModal = useCallback(() => {
    setLoginPromptReason('');
    setShowLoginModal(true);
  }, []);

  const handleOpenProfileModal = useCallback(() => {
    setShowProfileModal(true);
  }, []);

  const handleToggleDarkMode = useCallback(() => {
    setDarkMode((prev) => !prev);
  }, []);

  const handleCloseReviewModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleCloseLoginModal = useCallback(() => {
    setShowLoginModal(false);
    setLoginPromptReason('');
  }, []);

  const handleCloseProfileModal = useCallback(() => {
    setShowProfileModal(false);
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <Navbar
        onLoginClick={handleOpenLoginModal}
        onProfileClick={handleOpenProfileModal}
        onStateSelect={handleStateSelect}
        onReviewsClick={handleReviewsClick}
        darkMode={darkMode}
        onToggleDarkMode={handleToggleDarkMode}
      />

      <div className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5">
          <SearchBar onSearch={handleSearch} />
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-10">
        <section ref={reviewFeedRef} className="space-y-3 sm:space-y-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 sm:items-center sm:justify-between">
            <div>
              <h1 className="pl-2 sm:pl-6 text-2xl sm:text-3xl font-semibold text-black">{feedLabel}</h1>
            </div>
            <button
              type="button"
              onClick={handleAddReviewClick}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-200/70 transition hover:bg-slate-800 sm:w-auto"
            >
              + Add review
            </button>
          </div>

          <ReviewFeed
            reviews={reviews}
            stats={stats}
            pagination={pagination}
            loading={loading}
            pageLoading={pageLoading}
            error={error}
            plate={currentPlate}
            stateCode={currentState}
            onPageChange={handlePageChange}
            browsing={feedMode === 'browse'}
          />
        </section>
      </main>

      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 rounded-3xl border px-5 py-4 shadow-xl ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.message}
        </div>
      )}

      {showModal && (
        <ReviewModal
          onClose={handleCloseReviewModal}
          onSubmit={handleReviewSubmit}
          submitting={submitting}
          uploadProgress={uploadProgress}
        />
      )}

      {showLoginModal && !user && (
        <Login
          promptReason={loginPromptReason}
          onClose={handleCloseLoginModal}
        />
      )}

      {showProfileModal && user && (
        <UserProfile onClose={handleCloseProfileModal} />
      )}
    </div>
  );
}

export default AppContent;
